import { recordingsApi } from '@/core/api/endpoints/recordings';
import { processingTasksApi, type CreateTaskPayload } from '@/core/api/endpoints/processingTasks';
import { ApiError } from '@/core/api/types';
import type { AggregatedTaskState } from '@/core/models/processingTask';
import { usePendingTaskStore } from '@/core/stores/pendingTaskStore';
import { extForMime, type RecorderResult } from './recorder';
import { subscribeTask } from './taskDriver';

/**
 * 后端在额度耗尽时返回 HTTP 402 + `code=quota_exceeded`。
 * 调用方 catch 后用此 helper 判断是否要引导到会员页。
 */
export function isQuotaExceededError(err: unknown): boolean {
  if (err instanceof ApiError) {
    if (err.status === 402) return true;
    if (err.code === 'quota_exceeded') return true;
  }
  return false;
}

export interface SubmitTextInput {
  text: string;
  imageUrls?: string[];
  clientMetrics?: Record<string, unknown>;
}

export interface SubmitResult {
  taskId: string;
  /**
   * Resolves when the task reaches a terminal state. Optional — most callers
   * should ignore this and let the centralized driver continue in the
   * background while they navigate away.
   */
  done: Promise<AggregatedTaskState>;
}

function publishSeed(taskId: string, seed: Partial<AggregatedTaskState>): void {
  usePendingTaskStore.getState().upsert({
    task_id: taskId,
    status: 'pending',
    revision: 0,
    ...seed,
  });
}

function attachDriver(
  taskId: string,
  seed: Partial<AggregatedTaskState>
): Promise<AggregatedTaskState> {
  return new Promise((resolve) => {
    let unsubscribe: (() => void) | null = null;
    unsubscribe = subscribeTask(taskId, {
      seed,
      onTerminal: (s) => {
        unsubscribe?.();
        resolve(s);
      },
    });
  });
}

/**
 * Submit text + optional pre-uploaded image URLs. Returns immediately once
 * the backend acknowledges the task (so the caller can navigate). The task
 * itself continues in the background via the centralized task driver.
 */
export async function submitText(input: SubmitTextInput): Promise<SubmitResult> {
  const text = input.text.trim();
  const imageUrls = (input.imageUrls ?? []).filter(Boolean);
  const payload: CreateTaskPayload = {
    input_type: 'text',
    text: text || undefined,
    image_urls: imageUrls.length > 0 ? imageUrls : undefined,
    client_metrics: input.clientMetrics,
  };
  const task = await processingTasksApi.create(payload);
  const seed: Partial<AggregatedTaskState> = {
    status: task.status,
    stage: task.stage,
    progress: task.progress,
    image_urls: imageUrls.length > 0 ? imageUrls : undefined,
  };
  publishSeed(task.task_id, seed);
  const done = attachDriver(task.task_id, seed);
  return { taskId: task.task_id, done };
}

/**
 * Submit a recorded audio blob. Performs the OSS upload + confirm + task
 * creation, then hands off to the driver. Returns once the task_id is known
 * — the caller can then navigate away. The upload can take a few seconds;
 * we publish a placeholder state to the store so the UI can show progress.
 */
export async function submitAudio(rec: RecorderResult): Promise<SubmitResult> {
  const ts = Date.now();
  const ext = extForMime(rec.mimeType);
  const fileName = `web-${ts}.${ext}`;

  const { recording_id, upload_url, headers } = await recordingsApi.getUploadUrl(
    fileName,
    rec.mimeType
  );
  await recordingsApi.putToOss(upload_url, rec.blob, rec.mimeType, headers);
  await recordingsApi.confirmUpload(recording_id, {
    file_size: rec.blob.size,
    duration: Math.round(rec.durationMs / 1000),
  });

  const task = await processingTasksApi.create({
    input_type: 'audio',
    recording_id,
    client_metrics: {
      recording_duration_ms: Math.round(rec.durationMs),
    },
  });
  const seed: Partial<AggregatedTaskState> = {
    status: task.status,
    stage: task.stage,
    progress: task.progress,
  };
  publishSeed(task.task_id, seed);
  const done = attachDriver(task.task_id, seed);
  return { taskId: task.task_id, done };
}

// ----- Legacy compatibility shims (preserved so existing imports keep working) -----

export type OnState = (state: AggregatedTaskState) => void;

export interface ProcessTextInput extends SubmitTextInput {
  onState: OnState;
  signal?: AbortSignal;
}

/**
 * @deprecated Prefer `submitText`. This shim subscribes via the central driver
 * and forwards every state change to `onState` until terminal.
 */
export async function processText({
  text,
  imageUrls,
  clientMetrics,
  onState,
  signal,
}: ProcessTextInput): Promise<AggregatedTaskState> {
  const { taskId, done } = await submitText({ text, imageUrls, clientMetrics });
  return relayUntilDone(taskId, onState, done, signal);
}

/**
 * @deprecated Prefer `submitAudio`. Same shim shape as `processText`.
 */
export async function processAudio(
  rec: RecorderResult,
  onState: OnState,
  signal?: AbortSignal
): Promise<AggregatedTaskState> {
  const { taskId, done } = await submitAudio(rec);
  return relayUntilDone(taskId, onState, done, signal);
}

function relayUntilDone(
  taskId: string,
  onState: OnState,
  done: Promise<AggregatedTaskState>,
  signal?: AbortSignal
): Promise<AggregatedTaskState> {
  return new Promise((resolve, reject) => {
    let lastRevision = -1;
    const unsubscribeStore = usePendingTaskStore.subscribe((s) => {
      const st = s.tasks[taskId];
      if (!st) return;
      if (st.revision <= lastRevision) return;
      lastRevision = st.revision;
      onState(st);
    });
    const onAbort = () => {
      unsubscribeStore();
      reject(new DOMException('aborted', 'AbortError'));
    };
    if (signal) {
      if (signal.aborted) {
        onAbort();
        return;
      }
      signal.addEventListener('abort', onAbort, { once: true });
    }
    void done.then((final) => {
      unsubscribeStore();
      signal?.removeEventListener('abort', onAbort);
      resolve(final);
    });
    // emit current snapshot once
    const snap = usePendingTaskStore.getState().tasks[taskId];
    if (snap) onState(snap);
  });
}
